import { MutableRecordStore, StoreState } from '@deqo/foundation';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useObserver, useObserverReturningStore } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';

class FormStore extends MutableRecordStore({ dirty: false, working: false, rejections: 0 }) {
}

const FormContext = React.createContext<FormStore>(null);

export type FormState = Pick<StoreState<FormStore>, 'dirty' | 'working'>;
export const FormState = ({ dirty, working }: FormState) => ({ dirty, working });

export const useFormState = () => {
    const store = useObserverReturningStore(useContext(FormContext));
    return [
        FormState(store),
        useMemo(() => (state: Partial<FormState>) => void (store.mutate({ ...state, rejections: 0 })), [])
    ] as const;
};

export function useLeaveFormGuard<F extends (...args: any[]) => void>(func: F) {
    const store = useContext(FormContext);
    return useMemo(() => function (this: ThisParameterType<F>, ...args: Parameters<F>) {
        if (!store.dirty) {
            return func.apply(this, args);
        }
        else {
            store.mutate(x => x.rejections++);
            return undefined;
        }
    }, [store, func]);
}

export const FormBoundary: LFC = ({ children }) => {
    const store = useMemo(() => new FormStore(), []);
    return <FormContext.Provider value={store}>
        {children}
    </FormContext.Provider>;
};

export const ShowIfFormDirty: LFC = ({ children }) => {
    const { dirty, rejections } = useObserver(useContext(FormContext)) ?? {};
    const elmt = useRef<HTMLDivElement>();

    useEffect(() => {
        if (rejections > 0 && !!elmt.current) {
            elmt.current.animate([
                { transform: 'translateX(0)', offset: 0 },
                { transform: 'translateX(-10px)', offset: .08 },
                { transform: 'translateX(10px)', offset: .25 },
                { transform: 'translateX(-10px)', offset: .41 },
                { transform: 'translateX(10px)', offset: .58 },
                { transform: 'translateX(-5px)', offset: .75 },
                { transform: 'translateX(5px)', offset: .92 },
                { transform: 'translateX(0)', offset: 1 },
            ], {
                duration: 500,
                easing: 'linear',
                fill: 'forwards',
            });
        }
    }, [rejections]);

    return <div ref={elmt}>{dirty && children}</div>;
};
